import React from 'react';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import { defaultLineStyles } from 'styles/common';
import clsx from 'clsx';

const qsDefaultMinWidth = 120;
const QUANTITY_SELECTOR_SIZES = {
  small: 'small',
  medium: 'medium',
  large: 'large',
};

const useStyles = makeStyles((theme) => ({
  quantitySelector: {
    display: 'flex',
    backgroundColor: 'white',
    border: `1px solid ${theme.color.gray['15']}`,
    borderRadius: '4px',
    overflow: 'hidden',
    minWidth: `${qsDefaultMinWidth}px`,
  },
  quantityInput: {
    boxSizing: 'border-box',
    margin: 'auto',
    overflow: 'hidden',
    flexGrow: 1,
    textAlign: 'center',
  },
  quantitySelectorButton: {
    display: 'inline-block',
    border: 'none',
    background: '#FFF',
    position: 'relative',
    cursor: 'pointer',
    padding: '13px 20px',
    margin: 0,
    '&:hover, &:focus-visible': {
      outline: 'none',
      background: theme.color.gray['15'],
    },
  },
  minusAfter: {
    '&:after': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: '1px',
      background: theme.color.gray['15'],
      top: '33%',
      right: '0%',
      bottom: '33%',
    },
  },
  plusBefore: {
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      width: '1px',
      background: theme.color.gray['15'],
      top: '33%',
      left: '0%',
      bottom: '33%',
    },
  },
  small: {
    minHeight: '26px',
  },
  medium: {
    minHeight: '36px',
  },
  large: {
    minHeight: '48px',
  },
  line: defaultLineStyles.line,
  line2: defaultLineStyles.lineVert,
}));

function Disabled({ disabled, children }) {
  if (disabled) {
    return (
      <div style={{ opacity: 0.5, pointerEvents: 'none' }} disabled>
        {children}
      </div>
    );
  }

  return <React.Fragment>{children}</React.Fragment>;
}

// eslint-disable-next-line react/display-name
const QuantitySelector = React.forwardRef((props, ref) => {
  const {
    value = 0,
    ariaQuantityName = '',
    step = 1,
    onValueChange = () => {},
    minimumValue = 0,
    maximumValue = null,
    className = '',
    disabled = false,
    size = QUANTITY_SELECTOR_SIZES.small,
  } = props;

  const classes = useStyles();
  const { t } = useTranslation();

  const onMinusClick = () => {
    const newValue = value - step;
    if (minimumValue !== null && newValue < minimumValue) {
      return;
    }
    onValueChange(newValue);
  };

  const onPlusClick = () => {
    const newValue = value + step;
    if (maximumValue !== null && newValue > maximumValue) {
      return;
    }
    onValueChange(newValue);
  };

  return (
    <Disabled disabled={disabled}>
      <div
        ref={ref}
        className={`${classes.quantitySelector} ${className} ${classes[size]}`}
      >
        <button
          onClick={onMinusClick}
          className={clsx(
            classes.quantitySelectorButton,
            classes.minusAfter,
            'button-alt',
          )}
          aria-label={t('common.quantitySelector.minus', {
            name: ariaQuantityName,
          })}
        >
          <span className={classes.line} />
        </button>
        <div className={classes.quantityInput} aria-label={ariaQuantityName}>
          {value}
        </div>
        <button
          onClick={onPlusClick}
          className={clsx(
            classes.quantitySelectorButton,
            classes.plusBefore,
            'button-alt',
          )}
          aria-label={t('common.quantitySelector.plus', {
            name: ariaQuantityName,
          })}
        >
          <span className={classes.line} />
          <span className={`${classes.line} ${classes.line2}`} />
        </button>
      </div>
    </Disabled>
  );
});

export { qsDefaultMinWidth, QUANTITY_SELECTOR_SIZES };
export default QuantitySelector;
